import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dain753p14a1h3mr77eg";

type Status = "idle" | "loading" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubscribeForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const okCode = parsed?.code === "OK";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && okCode && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-6 text-center md:p-8">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-500 text-background-50 text-xl">
          <i className="ri-check-line"></i>
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
          订阅成功，欢迎入座
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          我们会在每周瓜单更新后把汇总邮件发到你的邮箱。随时可以回复邮件取消订阅，不会有任何打扰。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 whitespace-nowrap rounded-md border border-background-300/80 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
        >
          再订阅一个邮箱
        </button>
      </div>
    );
  }

  return (
    <form
      id="subscribe"
      data-readdy-form
      action={SUBMIT_URL}
      method="post"
      onSubmit={handleSubmit}
      className="scroll-mt-28 rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">订阅本周瓜单</h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        每周一次，把当周焦点事件一次性看完。我们只发送内容汇总，不会用于其他用途。
      </p>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row">
        <div className="flex-1">
          <label htmlFor="subscribe-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
            邮箱地址
          </label>
          <input
            id="subscribe-email"
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            autoComplete="email"
            className="w-full rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="sm:w-48">
          <label htmlFor="subscribe-interest" className="mb-1.5 block text-xs font-medium text-foreground-700">
            感兴趣的方向
          </label>
          <select
            id="subscribe-interest"
            name="interest"
            defaultValue="全部方向"
            className="w-full rounded-md border border-background-300/80 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="全部方向">全部方向</option>
            <option value="剧集风云">剧集风云</option>
            <option value="综艺观察">综艺观察</option>
            <option value="电影快报">电影快报</option>
            <option value="音乐现场">音乐现场</option>
            <option value="星光时尚">星光时尚</option>
            <option value="全网热议">全网热议</option>
          </select>
        </div>
      </div>

      <div className="guard-note-alt" aria-hidden="true">
        <label htmlFor="subscribe-website-alt">请勿填写此项</label>
        <input
          id="subscribe-website-alt"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3.5 py-2.5 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{formError}</span>
        </p>
      )}

      <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === "loading"}
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
          {status === "loading" ? "提交中…" : "提交订阅"}
        </button>
        <p className="text-xs text-foreground-500">
          提交即表示同意我们通过邮件发送瓜单汇总。
        </p>
      </div>
    </form>
  );
}